/**
 * Central MutationObserver that watches for DOM changes and
 * notifies registered selectors when matching elements appear.
 */

type WatchCallback = (elements: Element[]) => void;

interface WatchEntry {
  selector: string;
  callback: WatchCallback;
  seen: WeakSet<Element>;
}

const registry = new Map<string, WatchEntry>();
let observer: MutationObserver | null = null;

function scanAndNotify(entry: WatchEntry, root: Element | Document = document) {
  const elements = Array.from(root.querySelectorAll(entry.selector));
  const fresh = elements.filter((el) => !entry.seen.has(el));
  for (const el of fresh) entry.seen.add(el);
  if (fresh.length > 0) entry.callback(fresh);
}

function onMutation() {
  for (const entry of registry.values()) {
    scanAndNotify(entry);
  }
}

export function watchSelector(selector: string, callback: WatchCallback): void {
  const key = `${selector}::${callback.toString().slice(0, 32)}`;
  const entry: WatchEntry = { selector, callback, seen: new WeakSet() };
  registry.set(key, entry);
  // Initial scan
  scanAndNotify(entry);
}

export function unwatchSelector(selector: string): void {
  for (const [key] of registry) {
    if (key.startsWith(`${selector}::`)) registry.delete(key);
  }
}

export function startMutationObserver(): void {
  if (observer) return;
  observer = new MutationObserver(onMutation);
  observer.observe(document.body, { childList: true, subtree: true });
}

export function stopMutationObserver(): void {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  registry.clear();
}
